'use client'

import { Slider as SliderPrimitive } from '@base-ui/react/slider'
import * as React from 'react'
import { cn } from '../lib/utils'

interface SliderProps extends SliderPrimitive.Root.Props {
  /** Number of evenly-spaced stop indicators on the track */
  stops?: number
}

export function Slider({
  className,
  children,
  defaultValue,
  value,
  min = 0,
  max = 100,
  stops,
  ...props
}: SliderProps): React.ReactElement {
  const _values = React.useMemo(() => {
    if (value !== undefined) {
      // oxlint-disable-next-line typescript/no-unsafe-return -- slider value type is union with any[]; safe to return
      return Array.isArray(value) ? value : [value]
    }
    if (defaultValue !== undefined) {
      // oxlint-disable-next-line typescript/no-unsafe-return -- slider defaultValue type is union with any[]; safe to return
      return Array.isArray(defaultValue) ? defaultValue : [defaultValue]
    }
    return [min]
  }, [value, defaultValue, min])

  return (
    <SliderPrimitive.Root
      className={cn(
        'flex flex-col gap-1 has-[[data-dragging]]:cursor-grabbing data-disabled:cursor-not-allowed data-[orientation=horizontal]:w-full',
        className,
      )}
      defaultValue={defaultValue}
      max={max}
      min={min}
      thumbAlignment="edge"
      value={value}
      {...props}
    >
      {children}
      <SliderPrimitive.Control
        className="flex touch-none select-none has-[[data-dragging]]:cursor-grabbing data-disabled:pointer-events-none data-disabled:opacity-64 data-[orientation=horizontal]:w-full data-[orientation=horizontal]:min-w-44 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:flex-col"
        data-slot="slider-control"
      >
        <SliderPrimitive.Track
          className="relative grow select-none before:absolute before:rounded-full before:bg-current before:opacity-20 data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=horizontal]:before:inset-x-0.5 data-[orientation=horizontal]:before:inset-y-0 data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5 data-[orientation=vertical]:before:inset-x-0 data-[orientation=vertical]:before:inset-y-0.5"
          data-slot="slider-track"
        >
          <SliderPrimitive.Indicator
            className="bg-primary rounded-full select-none data-[orientation=horizontal]:ms-0.5 data-[orientation=vertical]:mb-0.5"
            data-slot="slider-indicator"
          />
          {/* oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no value" */}
          {stops != null &&
            stops > 1 &&
            getStops(stops).map((pct, i, arr) => (
              <span
                key={pct}
                className="bg-primary pointer-events-none absolute z-1 size-1 rounded-full"
                style={{
                  left:
                    i === 0
                      ? 2
                      : i === arr.length - 1
                        ? 'calc(100% - 5px)'
                        : `${pct}%`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
                data-slot="slider-stop"
              />
            ))}
          {Array.from({ length: _values.length }, (_, index) => (
            <SliderPrimitive.Thumb
              className="bg-primary has-focus-visible:ring-primary/80 block size-5 shrink-0 cursor-pointer rounded-full transition-[scale] outline-none select-none has-focus-visible:ring-[8px] data-dragging:scale-120 data-dragging:cursor-grabbing"
              data-slot="slider-thumb"
              index={index}
              key={String(index)}
            />
          ))}
        </SliderPrimitive.Track>
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  )
}

export function SliderValue({
  className,
  ...props
}: SliderPrimitive.Value.Props): React.ReactElement {
  return (
    <SliderPrimitive.Value
      className={cn('flex justify-end text-sm', className)}
      data-slot="slider-value"
      {...props}
    />
  )
}

function getStops(count: number): number[] {
  const step = 100 / (count - 1)
  const result: number[] = []
  for (let i = 0; i <= 100; i += step) result.push(Math.min(i, 100))
  if ((result.at(-1) ?? 0) < 100) result.push(100)
  return result
}

export { SliderPrimitive }
